/* @flow */
/* eslint-env es6,browser */

import * as u from '../utils';
import * as jsonp from '../jsonp';

/**
 * @description Implements functionality for breal header bidding
 * @exports bidders/brealbid
 * @requires {@link module:utils}
 * @requires {@link module:jsonp}
 */
class _brealbid {
  _name: string;
  _bInited: 1 | 0 | -1;
  _DefConfig: brealConfig;
  _config: brealConfig;
  _excludeList: Array<string>;
  _bidResponses: Object;

  //Member variable default initialization in constructor
  constructor(name: string, qpKey: string, cVar: string){
    this._name = name;
    this._bInited = u.F_NOT_INITED;
    this._DefConfig = u.extend(true, this._DefConfig, {
      positions : [],
      exclude : '',
      bidUrl : '',
      timeout : 2000,
      qpKey : qpKey, //not used only per position qpKey is used
      cVar : cVar,
      posTokenMap : {}
    });

    /**
     * @global
     * @typedef {Object} brealConfig
     * @property {Array<string>} positions
     * @property {string} [exclude]
     * @property {number} [timeout=2000]
     * @property {string} cVar
     * @property {Object} posTokenMap
     * @property {string} bidUrl
     */
    this._config = u.extend(true, this._DefConfig);
    this._excludeList = [];
    this._bidResponses = {};
  }

  /**
   * get list of bid positions supported by this header bidder
   * @param {string} pName
   * @return {Array<string>}
   */
  getBidPositions(pName: string) {
    if(u.isBrowserExcluded(pName, this._excludeList)) {
      return [];
    }
    return this._config.positions;
  }

  /**
   * Reset & Update config specific to this bidder
   * @param {brealConfig} oConfig
   */
  updateConfig(oConfig: brealConfig) {
    this._config = u.extend(true, {}, this._DefConfig, oConfig);

    //compute exclusion list again
    this._excludeList = u.getBrowserExcludeList(this._config.exclude);
  }

  /**
   * getter for qpkey
   * @param {string} pos
   */
  getQPKey(pos: string) {
    return u.isObject(this._config.posTokenMap[pos]) ? this._config.posTokenMap[pos].qpKey : '';
  }

  /**
   * getter for posKey
   * @param {string} pos
   */
  getPosKey(pos: string) {
    return u.isObject(this._config.posTokenMap[pos]) ? this._config.posTokenMap[pos].id : '';
  }

  /**
   * getter for cVar
   * @param {string} pos
   */
  getCVar() {
    return this._config.cVar;
  }

  _init(/**posList */) {
    return new Promise((resolve, reject) => {
      if (this._bInited === u.F_INITED) {
        resolve();
        return;
      }

      //Let's do a basic santiy check (script shdn't be empty etc.,)
      if (!this._config.bidUrl.length || !u.isObject(this._config.posTokenMap)) {
        u.logError(`${this._name} config missing mandatory data`);
        reject();
        return;
      }

      var misPos = this._config.positions.filter(pos => !this._config.posTokenMap[pos]);
      if (misPos.length) {
        u.logError(`${this._name} config positions missing in posTokenMap ${misPos.join(',')}`);
        reject();
        return;
      }

      this._bInited = u.F_INITED;
      resolve();
    });
  }

  /**
  * process bidresponse object
  * @access private
  */
  _processBidResponse(posList: Array<string>, ret: hbPartnerData) {
    var id = '', res = {};
    posList.forEach(pos => {
      id = this.getPosKey(pos),
      res = this._bidResponses[id];

      //res.ad - creative
      if (res && res.cpm && res.ad) {
        u.logDebug(`${this._name} id:${id} res.cpm:${res.cpm}`);
        ret[pos].info = `${id}|${res.cpm/10000}`;

        //query param string to pass to OpenX ad call
        ret[pos]._QP[this.getQPKey(pos)] = res.cpm / 10000;
        ret[pos]._CV[this._config.cVar] = id;
      }
    });
    return ret;
  }

  _getBid(pos: string) {
    // build params for bid request
    let params = {};
    params.data = {
      id: this.getPosKey(pos),
      size: `${this._config.posTokenMap[pos].width}x${this._config.posTokenMap[pos].height}`,
      referrer: document.location.href
    };
    params.logPrefix = pos;
    params.timeout = this._config.timeout;
    return jsonp.JSONP(this._config.bidUrl, params).then(data => {
      if (data && data.result) {
        this._bidResponses[this.getPosKey(pos)] = data.result;
      }
    });
  }

  _getAds(posList: Array<string>, ret: hbPartnerData) {
    this._bidResponses = {};

    //Create promise array with all bid requests
    let pArray = posList.map(pos => this._getBid(pos));

    //Resolve all accumulated promise here
    return u.allSettledPromise(pArray)
      .then(arr => {
        let fValues = arr.filter(v => v.state !== 'fulfilled').map(v => v.reason);
        if (fValues.length) {  //Log all failed promise
          u.logError(`${this._name} bidding errors: ${fValues.join(',')}`);
        }
        return this._processBidResponse(posList, ret);
      }, error => {
        u.logError(`${this._name} bidding failed: ${error || ''}`);
        return ret;
      });
  }

  /**
   * get bids for current partner
   * @param {Array<string>} posList - list of ad positions
   * @param {Object.<string, hbPosData>} ret - header bidding default response
   * @return {Promise<Object.<string, hbPosData>>}
   */
  getBids(posList: Array<string>, ret: hbPartnerData) {
    return this._init(posList).then(() => {
      //get bids
      u.logDebug(`${this._name} Init is done. getting ad bids`);
      return this._getAds(posList, ret);
    }, () => {
      u.logError(`${this._name} init failed`);
      return ret;
    });
  }

 /**
  * render ad for this partner
  * @param {window} win - window object
  * @param {window.document} doc - document object
  * @param {string} pos - ad position
  * @param {string} id - position specific id
  */
  renderAd(win: window, doc: window.document, pos: string, id: string) {
    try {
      var newIf = u.createIframe(doc, `${this._name}-${id}`, '100%', '100%', this._bidResponses[id].ad);
      doc.body.appendChild(newIf);
    } catch(e) {
      u.logDebug(`${this._name} render ad failure ${e.message}`);
    }
  }
}

//export singleton object as default for this module
let breal = new _brealbid('breal', 'BIDDR_BID', 'BIDDR_VAR');
export {_brealbid, breal};
